import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';

const useStyles = makeStyles<{ isExpanded: boolean }>()((theme: Theme, { isExpanded }) => ({
  dialogContent: {
    padding: theme.spacing(0),
    minHeight: '60vh',
    maxHeight: isExpanded ? '100vh' : '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
    overflow: 'hidden',
  },
  metadataSection: {
    flexShrink: 0,
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  subject: {
    fontSize: '18px',
    fontWeight: 500,
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  metadataRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    alignItems: 'flex-start',
  },
  label: {
    fontWeight: 600,
    minWidth: '40px',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  value: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    wordBreak: 'break-word',
  },
  emailBodyContainer: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  iframe: {
    flex: 1,
    width: '100%',
    border: 'none',
    minHeight: '300px',
  },
}));

interface EmailModalProps {
  open: boolean;
  onClose: () => void;
  data: {
    title?: string;
    subject?: string;
    sender?: string;
    tos?: Array<{ email?: string }>;
    ccs?: Array<{ email?: string }>;
    bccs?: Array<{ email?: string }>;
    notes?: string;
  };
}

export const EmailModal = ({ open, onClose, data }: EmailModalProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { classes } = useStyles({ isExpanded });

  const isEmail = data?.title === 'Email Notes';
  const toEmails = data?.tos?.map(r => r?.email).filter(Boolean).join('; ');
  const ccEmails = data?.ccs?.filter(Boolean).map(r => r?.email).filter(Boolean).join('; ');
  const bccEmails = data?.bccs?.filter(Boolean).map(r => r?.email).filter(Boolean).join('; ');

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      variant="blue"
      dialogTitleProps={{
        text: isEmail ? t('Email') : (data?.title ?? ''),
        closeButton: true,
        expandable: true,
        onExpandChange: setIsExpanded,
      }}
      dialogContentProps={{
        classes: { root: classes.dialogContent },
      }}
    >
      <Box className={classes.container}>
        {isEmail && (
          <Box className={classes.metadataSection}>
            <Typography className={classes.subject}>{data?.subject}</Typography>

            {data?.sender && (
              <Box className={classes.metadataRow}>
                <Typography className={classes.label}>{t('From:')}</Typography>
                <Typography className={classes.value}>{data.sender}</Typography>
              </Box>
            )}

            {toEmails && (
              <Box className={classes.metadataRow}>
                <Typography className={classes.label}>{t('To:')}</Typography>
                <Typography className={classes.value}>{toEmails}</Typography>
              </Box>
            )}

            {ccEmails && (
              <Box className={classes.metadataRow}>
                <Typography className={classes.label}>{t('CC:')}</Typography>
                <Typography className={classes.value}>{ccEmails}</Typography>
              </Box>
            )}

            {bccEmails && (
              <Box className={classes.metadataRow}>
                <Typography className={classes.label}>{t('BCC:')}</Typography>
                <Typography className={classes.value}>{bccEmails}</Typography>
              </Box>
            )}
          </Box>
        )}

        <Box className={classes.emailBodyContainer}>
          <iframe
            title={data?.title ?? 'Email Preview'}
            className={classes.iframe}
            srcDoc={data?.notes ?? ''}
          />
        </Box>
      </Box>
    </GenericDialog>
  );
};
